import { For, Show, createSignal } from "solid-js";

import { useDeal, useDeals } from "~/lib/hooks";

type Props = {
  wsKey: () => string;
  value: () => string | null;
  onChange: (dealId: string | null, name: string | null) => void;
  selectedName?: () => string | null;
  label?: string;
  placeholder?: string;
  size?: "sm" | "md";
  disabled?: boolean;
};

export function DealPicker(props: Props) {
  const [query, setQuery] = createSignal("");
  const [open, setOpen] = createSignal(false);

  const deals = useDeals(props.wsKey, () => ({
    q: query() || undefined,
    limit: 12,
  }));

  // Auto-resolve the selected deal's name when the parent didn't pre-supply
  // one (same pattern as Account/Contact/Product/UserPicker).
  const resolved = useDeal(props.wsKey, () => props.value() ?? "");

  const chipLabel = () => {
    const supplied = props.selectedName?.();
    if (supplied) return supplied;
    if (resolved.data) return resolved.data.name;
    return props.value()?.slice(0, 14) ?? "Deal";
  };

  const size = () => props.size ?? "md";
  const inputCls = () =>
    size() === "sm" ? "input input-bordered input-sm w-full" : "input input-bordered w-full";

  const clear = () => {
    props.onChange(null, null);
    setQuery("");
  };

  const pick = (id: string, name: string) => {
    props.onChange(id, name);
    setQuery("");
    setOpen(false);
  };

  return (
    <div class="relative">
      <Show
        when={props.value()}
        fallback={
          <div>
            <input
              type="text"
              class={inputCls()}
              placeholder={props.placeholder ?? "Search deals…"}
              aria-label={props.label ?? "Search deals"}
              value={query()}
              disabled={props.disabled}
              onInput={(e) => {
                setQuery(e.currentTarget.value);
                setOpen(true);
              }}
              onFocus={() => setOpen(true)}
              onBlur={() => {
                window.setTimeout(() => setOpen(false), 150);
              }}
              autocomplete="off"
            />
            <Show when={open() && query().length > 0}>
              <ul
                role="listbox"
                aria-label="Deal matches"
                class="absolute z-10 mt-1 w-full max-h-56 overflow-y-auto rounded-lg bg-base-100 shadow-lg border border-base-300"
              >
                <For each={deals.data?.items ?? []}>
                  {(d) => (
                    <li>
                      <button
                        type="button"
                        role="option"
                        aria-selected="false"
                        class="w-full text-left px-3 py-2 hover:bg-base-200"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => pick(d.id, d.name)}
                      >
                        <div class="font-medium">{d.name}</div>
                        <Show when={d.amount !== null}>
                          <div class="text-xs opacity-60">
                            {d.amount?.toFixed(2)} {d.currency ?? ""} · {d.status}
                          </div>
                        </Show>
                      </button>
                    </li>
                  )}
                </For>
                <Show when={(deals.data?.items.length ?? 0) === 0 && !deals.isLoading}>
                  <li class="px-3 py-2 text-sm opacity-60">No deals match.</li>
                </Show>
                <Show when={deals.isLoading}>
                  <li class="px-3 py-2 text-sm opacity-60">Searching…</li>
                </Show>
              </ul>
            </Show>
          </div>
        }
      >
        <div
          class={`badge ${
            size() === "sm" ? "badge-md" : "badge-lg"
          } badge-primary badge-outline gap-2`}
        >
          <span class="truncate max-w-xs">{chipLabel()}</span>
          <button
            type="button"
            class="btn btn-ghost btn-xs btn-circle"
            aria-label="Clear deal"
            disabled={props.disabled}
            onClick={clear}
          >
            ✕
          </button>
        </div>
      </Show>
    </div>
  );
}
